import { create } from 'zustand';
import { AuthUser } from '@/types/auth.types';
import { TOKEN_KEY, USER_KEY, TENANT_KEY } from '@/config/app.config';
import { authAPI } from '@/services/auth.service';
import { LoginPayload } from '@/schemas/login.schema';

interface AuthState {
    token: string | null;
    user: AuthUser | null;
    tenant?: any | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    isLoggingIn: boolean;
    error: string | null;

    setToken: (token: string) => void;
    setUser: (user: AuthUser) => void;
    setLoading: (loading: boolean) => void;
    setIsLoggingIn: (state: boolean) => void;
    setError: (error: string | null) => void;

    login: (payload: LoginPayload) => Promise<any>;
    logout: () => void;
    logoutServer: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
    token: localStorage.getItem(TOKEN_KEY),
    user: JSON.parse(localStorage.getItem(USER_KEY) || 'null'),
    tenant: JSON.parse(localStorage.getItem(TENANT_KEY) || 'null'),
    isAuthenticated: !!localStorage.getItem(TOKEN_KEY),
    isLoading: false,
    isLoggingIn: false,

    error: null,

    setToken: (token) => {
        localStorage.setItem(TOKEN_KEY, token);
        set({ token, isAuthenticated: true });
    },
    setUser: (user) => {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
        set({ user, isAuthenticated: true });
    },
    // Server-driven login that updates local storage and store state
    login: async (payload: LoginPayload) => {
        set({ isLoggingIn: true });
        try {
            const resp = await authAPI.login(payload);
            const data = (resp as any).data || resp;

            if (!data || !data.user || !data.accessToken) {
                throw new Error('Invalid login response');
            }

            localStorage.setItem(TOKEN_KEY, data.accessToken);
            localStorage.setItem('vldl_refresh_token', data.refreshToken || '');
            localStorage.setItem(USER_KEY, JSON.stringify(data.user));
            localStorage.setItem(TENANT_KEY, JSON.stringify(data.tenant || null));

            set({ token: data.accessToken, user: data.user, isAuthenticated: true });

            return data;
        } finally {
            set({ isLoggingIn: false });
        }
    },
    setLoading: (loading) => set({ isLoading: loading }),
    setIsLoggingIn: (loading) => set({ isLoggingIn: loading }),
    setError: (error) => set({ error }),

    logout: () => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem('vldl_refresh_token');
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(TENANT_KEY);
        set({ token: null, user: null, isAuthenticated: false });
    },
    // Server-side logout helper
    logoutServer: async () => {
        try {
            await authAPI.logout();
        } catch (e) {
            // ignore server errors but continue to clear client state
            console.error('Logout API error', e);
        } finally {
            useAuthStore.getState().logout();
        }
    },
}));
